import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ShareCartPayload } from '@cart/const';
import { ShareCartFacade } from '@cart/data-access';
import { SENTRY } from '@core/const';
import { ModalTooltipService, SdvorIconComponent } from '@sdvor/shared/ui/v2';
import { CustomShareData } from '@shared/models';
import { BrowserNavigatorService, DeviceService } from '@shared/services';
import { IconTypes } from '@shared/v2';
import { filter, take } from 'rxjs/operators';
import { CartShareTooltipComponent } from '../../cart-share-tooltip';

@Component({
  selector: 'sd-cart-action-share',
  imports: [SdvorIconComponent],
  templateUrl: './cart-action-share.component.html',
  styleUrls: ['./cart-action-share.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartActionShareComponent {
  private readonly deviceService = inject(DeviceService);
  private readonly shareCartFacade = inject(ShareCartFacade);
  private readonly browserNavigatorService = inject(BrowserNavigatorService);
  private readonly modalTooltipService = inject(ModalTooltipService);
  private readonly sentry = inject(SENTRY);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isTabletMdDown = toSignal(this.deviceService.isTabletMdDown$, { initialValue: false });
  protected readonly iconTypes = IconTypes;

  handleClick(): void {
    this.shareCartFacade.shareCart();

    if (this.isTabletMdDown()) {
      this.handleNativeShare();
    } else {
      this.handleDesktopShare();
    }
  }

  private handleDesktopShare(): void {
    this.shareCartFacade
      .getShareCartValue()
      .pipe(filter(Boolean), take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((payload) => {
        if (!payload?.url) return;

        this.browserNavigatorService.copyToClipboard(payload.url).subscribe({
          complete: () => {
            this.modalTooltipService.open(CartShareTooltipComponent, { lifeDuration: 3000 });
          },
          // Если скопировать не удалось — тултип не показываем
        });
      });
  }

  private handleNativeShare(): void {
    this.shareCartFacade
      .getShareCartValue()
      .pipe(filter<ShareCartPayload>(Boolean), take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((payload) => {
        const shareData: CustomShareData = {
          title: 'Корзина Строительный двор',
          url: payload.url,
        };

        this.browserNavigatorService.nativeShare(shareData).subscribe({
          error: (err) => {
            if (!(err instanceof TypeError)) {
              this.sentry.captureException(err);
            }
          },
        });
      });
  }
}
